import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  Article as ArticleIcon,
  QuestionAnswer as QAIcon,
  PhotoCamera as PhotoIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../services/api';

// Calculate if a snapshot is successful - moved before component to fix reference error
const calculateSnapshotSuccess = (snapshot) => {
  console.log('🔍 Calculating success for snapshot:', snapshot._id);
  console.log('🔍 Snapshot rawData keys:', Object.keys(snapshot.rawData || {}));
  
  const dataTypes = ['business', 'reviews', 'posts', 'qa', 'photos'];
  const results = {};
  
  dataTypes.forEach(type => {
    if (type === 'business') {
      // Check business info - look for listing data with business information
      const listingData = snapshot.rawData?.listing;
      console.log(`🔍 Business listing data:`, listingData);
      
      // Check if the API call was successful (success: true and status: 200)
      if (listingData && listingData.success === true && listingData.status === 200) {
        // For business, we consider it successful if the API call succeeded
        // The actual business data might be in different nested structures
        results[type] = true;
        console.log(`🔍 Business marked as successful: API call succeeded`);
      } else {
        results[type] = false;
        console.log(`🔍 Business failed: success=${listingData?.success}, status=${listingData?.status}`);
      }
    } else {
      const data = snapshot.rawData?.[type];
      console.log(`🔍 ${type} data:`, data);
      
      // Check if the API call was successful (success: true and status: 200)
      if (data && data.success === true && data.status === 200) {
        // For other data types, we consider them successful if the API call succeeded
        // The actual content might be empty arrays, but that's still a successful API response
        results[type] = true;
        console.log(`🔍 ${type} marked as successful: API call succeeded`);
      } else {
        results[type] = false;
        console.log(`🔍 ${type} failed: success=${data?.success}, status=${data?.status}`);
      }
    }
  });

  const successfulTypes = Object.values(results).filter(Boolean).length;
  const isFullySuccessful = successfulTypes === dataTypes.length;
  const isFullyFailed = successfulTypes === 0;

  const result = {
    ...results,
    successfulTypes,
    totalTypes: dataTypes.length,
    isFullySuccessful,
    isFullyFailed,
    isPartial: !isFullySuccessful && !isFullyFailed
  };
  
  console.log(`🔍 Final result for snapshot ${snapshot._id}:`, result);
  return result;
};

const SnapshotSuccessTracker = React.memo(({ profiles, paused = false }) => {
  console.log('🎯 SnapshotSuccessTracker component rendered with', profiles.length, 'profiles', paused ? '(paused)' : '');
  
  const [snapshots, setSnapshots] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [retryingSnapshots, setRetryingSnapshots] = useState(new Set());
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0 });
  const [failedTableExpanded, setFailedTableExpanded] = useState(false);
  const [successTableExpanded, setSuccessTableExpanded] = useState(false);
  
  // Cache for snapshots to avoid unnecessary re-fetching
  const snapshotCache = useRef({});
  const lastFetchTime = useRef(0);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  
  // State for UI display of cache age
  const [cacheAge, setCacheAge] = useState(0);
  
  // Ref to prevent multiple chunking operations from running simultaneously
  const isChunking = useRef(false);
  
  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  // Fetch snapshots for all tracked profiles - OPTIMIZED to use single API call
  useEffect(() => {
    console.log('🔄 useEffect triggered with', profiles.length, 'profiles', paused ? '(paused)' : '');

    if (paused) {
      console.log('⏸️ SnapshotSuccessTracker paused — skipping snapshot fetch');
      isChunking.current = false;
      return;
    }
    
    const fetchSnapshots = async () => {
      try {
        const trackedProfiles = profiles.filter(p => p.isTracked);
        console.log('🔄 Found', trackedProfiles.length, 'tracked profiles');
        
        if (trackedProfiles.length === 0) {
          console.log('🔄 No tracked profiles, setting empty snapshots');
          setSnapshots({});
          setLoading(false);
          return;
        }

        // Check if we have fresh cached data
        const now = Date.now();
        const hasFreshCache = lastFetchTime.current > 0 && 
                             (now - lastFetchTime.current) < CACHE_DURATION &&
                             Object.keys(snapshotCache.current).length > 0;
        
        if (hasFreshCache) {
          console.log('🚀 Using cached snapshot data (fresh for', Math.round((now - lastFetchTime.current) / 1000), 'seconds)');
          setSnapshots(snapshotCache.current);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        setFetchProgress({ current: 0, total: trackedProfiles.length });

        // OPTIMIZATION: Fetch latest snapshots only (much more efficient)
        try {
          // Prevent multiple chunking operations from running simultaneously
          if (isChunking.current) {
            console.log('🔄 Chunking already in progress, skipping...');
            return;
          }
          
          isChunking.current = true;
          
          const profileIds = trackedProfiles.map(p => p._id);
          console.log(`🚀 Fetching LATEST snapshots for ${trackedProfiles.length} profiles...`);
          console.log('🔍 Profile IDs being sent:', profileIds.slice(0, 3), '...');
          console.log('🔍 About to make chunked LATEST API calls...');
          
          // Show immediate progress for bulk fetch
          setFetchProgress({ current: Math.floor(trackedProfiles.length / 2), total: trackedProfiles.length });
          
          // Split into chunks of 50 profiles to avoid URL length issues
          const CHUNK_SIZE = 50;
          const chunks = [];
          for (let i = 0; i < profileIds.length; i += CHUNK_SIZE) {
            chunks.push(profileIds.slice(i, i + CHUNK_SIZE));
          }
          
          console.log(`🔍 Split into ${chunks.length} chunks of ${CHUNK_SIZE} profiles each`);
          
          // Fetch latest snapshots for each chunk - process sequentially to avoid race conditions
          const allLatestSnapshots = [];
          for (let i = 0; i < chunks.length; i++) {
            if (pausedRef.current) {
              console.log('⏸️ Snapshot fetch interrupted — live export started');
              break;
            }

            const chunk = chunks[i];
            console.log(`🔍 Fetching chunk ${i + 1}/${chunks.length} with ${chunk.length} profiles`);
            
            try {
              // Use URLSearchParams to properly serialize the array
              const params = new URLSearchParams();
              chunk.forEach(id => params.append('profileIds', id));
              console.log('🔍 Generated URL params:', params.toString());
              
              // Use the new /latest endpoint for much better performance
              const response = await api.get('/api/snapshots/latest', {
                params: params
              });
              
              const chunkSnapshots = response.data;
              allLatestSnapshots.push(...chunkSnapshots);
              console.log(`✅ Chunk ${i + 1} completed: ${chunkSnapshots.length} latest snapshots`);
              
              // Update progress - batch updates to prevent excessive re-renders
              const progress = Math.floor((i + 1) * (trackedProfiles.length / chunks.length));
              setFetchProgress({ current: progress, total: trackedProfiles.length });
            } catch (chunkErr) {
              console.error(`❌ Chunk ${i + 1} failed:`, chunkErr);
              console.error('Chunk error details:', {
                chunkIndex: i + 1,
                chunkSize: chunk.length,
                status: chunkErr.response?.status,
                message: chunkErr.message,
                data: chunkErr.response?.data
              });
              // Continue remaining chunks instead of aborting the whole tracker
              continue;
            }
          }
          
          console.log(`🔍 All chunks completed! Total latest snapshots: ${allLatestSnapshots.length}`);
          
          // Debug: Log what we received from the backend
          console.log('🔍 Backend response data structure:', allLatestSnapshots);
          console.log('🔍 Sample snapshot object:', allLatestSnapshots[0]);
          console.log('🔍 Checking for specific profile 68891240d2845576c459f12c...');
          const specificProfile = allLatestSnapshots.find(s => s && s.profileId === '68891240d2845576c459f12c');
          console.log('🔍 Found specific profile in response:', specificProfile);
           
          // Debug: Check the data structure more thoroughly
          console.log('🔍 Total snapshots received:', allLatestSnapshots.length);
          console.log('🔍 First few snapshots:', allLatestSnapshots.slice(0, 3));
          console.log('🔍 Are all snapshots null?', allLatestSnapshots.every(s => s === null));
          console.log('🔍 Non-null snapshots:', allLatestSnapshots.filter(s => s !== null).length);
          console.log('🔍 Sample non-null snapshot:', allLatestSnapshots.find(s => s !== null));
           
          // Group snapshots by profile ID - now each profile has only its latest snapshot
          const snapshotsData = {};
          trackedProfiles.forEach(profile => {
            const latestSnapshot = allLatestSnapshots.find(s => s && s.profileId === profile._id);
            // Store as array for consistency, empty array if no snapshot (null from backend)
            snapshotsData[profile._id] = latestSnapshot ? [latestSnapshot] : [];
            
            // Debug: Log what we found for each profile
            if (profile._id === '68891240d2845576c459f12c') {
              console.log(`🔍 DEBUG: Profile ${profile.name} (${profile._id}) - found snapshot:`, latestSnapshot);
              console.log(`🔍 DEBUG: Will store as:`, latestSnapshot ? [latestSnapshot] : []);
            }
          });
          
          // Debug: Check final snapshotsData structure
          console.log('🔍 Final snapshotsData keys:', Object.keys(snapshotsData));
          console.log('🔍 snapshotsData sample entries:', Object.entries(snapshotsData).slice(0, 3));
          console.log('🔍 Profiles with snapshots in snapshotsData:', Object.values(snapshotsData).filter(arr => arr.length > 0).length);
          
          // Update cache and state
          snapshotCache.current = snapshotsData;
          setSnapshots(snapshotsData);
          lastFetchTime.current = now;
          setCacheAge(now);
          setFetchProgress({ current: trackedProfiles.length, total: trackedProfiles.length });
          
        } catch (err) {
          console.error('Failed to fetch snapshots:', err);
          console.error('Error details:', {
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data,
            message: err.message,
            url: err.config?.url,
            params: err.config?.params
          });
          
          // Don't fall back to individual calls - show error instead
          setError(`Failed to fetch snapshots: ${err.response?.data?.error || err.message}`);
          setLoading(false);
        } finally {
          // Always reset the chunking flag
          isChunking.current = false;
        }
      } catch (err) {
        console.error('Error fetching snapshots:', err);
        setError('Failed to load snapshot data');
      } finally {
        setLoading(false);
      }
    };

    if (profiles.length > 0) {
      fetchSnapshots();
    }
  }, [profiles, paused, CACHE_DURATION]);

  // Calculate snapshot success metrics
  const successMetrics = useMemo(() => {
    // Don't calculate metrics while loading or if no snapshots yet
    if (loading || Object.keys(snapshots).length === 0) {
      console.log('📊 Skipping metrics calculation - loading:', loading, 'snapshots count:', Object.keys(snapshots).length);
      return {
        totalProfiles: 0,
        profilesWithSnapshots: 0,
        totalSnapshots: 0,
        successfulSnapshots: 0,
        failedSnapshots: 0,
        partialSnapshots: 0,
        successRate: 0,
        profilesByStatus: {
          allSuccessful: [],
          allFailed: [],
          partial: [],
          noSnapshots: []
        }
      };
    }

    console.log('📊 Calculating success metrics for', Object.keys(snapshots).length, 'snapshot entries');
    
    const metrics = {
      totalProfiles: 0,
      profilesWithSnapshots: 0,
      totalSnapshots: 0,
      successfulSnapshots: 0,
      failedSnapshots: 0,
      partialSnapshots: 0,
      successRate: 0,
      profilesByStatus: {
        allSuccessful: [],
        allFailed: [],
        partial: [],
        noSnapshots: []
      }
    };

    const trackedProfiles = profiles.filter(p => p.isTracked);
    metrics.totalProfiles = trackedProfiles.length;

    trackedProfiles.forEach(profile => {
      const profileSnapshots = snapshots[profile._id] || [];
      
      if (profileSnapshots.length === 0) {
        console.log(`📊 Profile ${profile.name} (${profile._id}) has no snapshots`);
        metrics.profilesByStatus.noSnapshots.push(profile);
        return;
      }

      metrics.profilesWithSnapshots++;
      metrics.totalSnapshots += profileSnapshots.length;

      // Get the latest snapshot (now it's the only one in the array)
      const latestSnapshot = profileSnapshots[0]; // Since we only fetch the latest
      console.log(`📊 Profile ${profile.name} (${profile._id}) has ${profileSnapshots.length} snapshots, latest:`, latestSnapshot._id);

      if (latestSnapshot) {
        const snapshotSuccess = calculateSnapshotSuccess(latestSnapshot);
        console.log(`📊 Profile ${profile.name} snapshot success:`, {
          business: snapshotSuccess.business,
          reviews: snapshotSuccess.reviews,
          posts: snapshotSuccess.posts,
          qa: snapshotSuccess.qa,
          photos: snapshotSuccess.photos,
          isFullySuccessful: snapshotSuccess.isFullySuccessful,
          isFullyFailed: snapshotSuccess.isFullyFailed,
          successfulTypes: snapshotSuccess.successfulTypes
        });
        
        if (snapshotSuccess.isFullySuccessful) {
          metrics.successfulSnapshots++;
          // Since we only have the latest snapshot, we can't check if ALL are successful
          // We'll assume if the latest is successful, the profile is generally successful
          metrics.profilesByStatus.allSuccessful.push(profile);
          console.log(`✅ Profile ${profile.name} marked as fully successful`);
        } else if (snapshotSuccess.isFullyFailed) {
          metrics.failedSnapshots++;
          // Since we only have the latest snapshot, we can't check if ALL are failed
          // We'll assume if the latest is failed, the profile is generally failed
          metrics.profilesByStatus.allFailed.push(profile);
          console.log(`❌ Profile ${profile.name} marked as fully failed`);
        } else {
          metrics.partialSnapshots++;
          metrics.profilesByStatus.partial.push(profile);
          console.log(`⚠️ Profile ${profile.name} marked as partial`);
        }
      }
    });

    if (metrics.totalSnapshots > 0) {
      metrics.successRate = Math.round((metrics.successfulSnapshots / metrics.totalSnapshots) * 100);
    }

    console.log('📊 Final metrics calculated:', metrics);
    return metrics;
  }, [profiles, snapshots, loading]);

  // Retry failed snapshots for a profile
  const handleRetrySnapshots = useCallback(async (profileId) => {
    try {
      setRetryingSnapshots(prev => new Set(prev).add(profileId));
      setError(null);
      setSuccessMessage(null);

      const response = await api.post('/api/snapshots/capture', {
        profileId: profileId
      });

      if (response.data.success) {
        // Refresh snapshots for this profile
        const refreshResponse = await api.get(`/api/snapshots/profile/${profileId}`);
        setSnapshots(prev => ({
          ...prev,
          [profileId]: refreshResponse.data
        }));
        setSuccessMessage('Snapshots retried successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        setError('Failed to retry snapshots');
      }
    } catch (err) {
      console.error('Error retrying snapshots:', err);
      setError('Failed to retry snapshots: ' + (err.response?.data?.error || err.message));
    } finally {
      setRetryingSnapshots(prev => {
        const newSet = new Set(prev);
        newSet.delete(profileId);
        return newSet;
      });
    }
  }, []);

  // Retry all failed snapshots
  const handleRetryAllFailed = useCallback(async () => {
    try {
      setError(null);
      setSuccessMessage(null);
      const failedProfiles = [
        ...successMetrics.profilesByStatus.allFailed,
        ...successMetrics.profilesByStatus.partial
      ];

      if (failedProfiles.length === 0) {
        setError('No failed snapshots to retry');
        return;
      }

      // Retry snapshots for all failed profiles
      const retryPromises = failedProfiles.map(profile => 
        api.post('/api/snapshots/capture', { profileId: profile._id })
      );

      await Promise.allSettled(retryPromises);

      // Refresh all snapshots
      const newSnapshots = {};
      for (const profile of failedProfiles) {
        try {
          const response = await api.get(`/api/snapshots/profile/${profile._id}`);
          newSnapshots[profile._id] = response.data;
        } catch (err) {
          console.error(`Failed to refresh snapshots for ${profile._id}:`, err);
        }
      }

      setSnapshots(prev => ({ ...prev, ...newSnapshots }));
      setSuccessMessage(`Successfully retried snapshots for ${failedProfiles.length} profiles!`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error retrying all failed snapshots:', err);
      setError('Failed to retry all failed snapshots');
    }
  }, [successMetrics.profilesByStatus.allFailed, successMetrics.profilesByStatus.partial]);

  // Open profile details dialog
  const handleOpenDetails = useCallback((profile) => {
    setSelectedProfile(profile);
    setDetailsOpen(true);
  }, []);

  // Refresh all snapshots
  const handleRefreshData = useCallback(async (forceRefresh = false) => {
    try {
      const trackedProfiles = profiles.filter(p => p.isTracked);
      
      if (trackedProfiles.length === 0) {
        setSnapshots({});
        setLoading(false);
        return;
      }

      // Check cache unless force refresh is requested
      if (!forceRefresh) {
        const now = Date.now();
        const hasFreshCache = lastFetchTime.current > 0 && 
                             (now - lastFetchTime.current) < CACHE_DURATION &&
                             Object.keys(snapshotCache.current).length > 0;
        
        if (hasFreshCache) {
          console.log('🚀 Using cached snapshot data for refresh (fresh for', Math.round((now - lastFetchTime.current) / 1000), 'seconds)');
          setSnapshots(snapshotCache.current);
          return;
        }
      }

      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setFetchProgress({ current: 0, total: trackedProfiles.length });

      // Try bulk fetch first
      try {
        // Prevent multiple chunking operations from running simultaneously
        if (isChunking.current) {
          console.log('🔄 Chunking already in progress, skipping refresh...');
          return;
        }
        
        isChunking.current = true;
        
        const profileIds = trackedProfiles.map(p => p._id);
        console.log(`🔄 Refreshing LATEST snapshots for ${trackedProfiles.length} profiles...`);
        console.log('🔍 Profile IDs being sent:', profileIds.slice(0, 3), '...');
        
        // Show immediate progress for bulk fetch
        setFetchProgress({ current: Math.floor(trackedProfiles.length / 2), total: trackedProfiles.length });
        
        // Split into chunks of 50 profiles to avoid URL length issues
        const CHUNK_SIZE = 50;
        const chunks = [];
        for (let i = 0; i < profileIds.length; i += CHUNK_SIZE) {
          chunks.push(profileIds.slice(i, i + CHUNK_SIZE));
        }
        
        console.log(`🔍 Split into ${chunks.length} chunks of ${CHUNK_SIZE} profiles each`);
        
        // Fetch latest snapshots for each chunk - process sequentially to avoid race conditions
        const allLatestSnapshots = [];
        for (let i = 0; i < chunks.length; i++) {
          const chunk = chunks[i];
          console.log(`🔍 Fetching chunk ${i + 1}/${chunks.length} with ${chunk.length} profiles`);
          
          try {
            // Use URLSearchParams to properly serialize the array
            const params = new URLSearchParams();
            chunk.forEach(id => params.append('profileIds', id));
            console.log('🔍 Generated URL params:', params.toString());
            
            // Use the new /latest endpoint for much better performance
            const response = await api.get('/api/snapshots/latest', {
              params: params
            });
            
            const chunkSnapshots = response.data;
            allLatestSnapshots.push(...chunkSnapshots);
            console.log(`✅ Chunk ${i + 1} completed: ${chunkSnapshots.length} latest snapshots`);
            
            // Update progress - batch updates to prevent excessive re-renders
            const progress = Math.floor((i + 1) * (trackedProfiles.length / chunks.length));
            setFetchProgress({ current: progress, total: trackedProfiles.length });
          } catch (chunkErr) {
            console.error(`❌ Chunk ${i + 1} failed:`, chunkErr);
            console.error('Chunk error details:', {
              chunkIndex: i + 1,
              chunkSize: chunk.length,
              status: chunkErr.response?.status,
              message: chunkErr.message,
              data: chunkErr.response?.data
            });
            
            // If any chunk fails, stop and show error
            throw new Error(`Chunk ${i + 1} failed: ${chunkErr.response?.data?.error || chunkErr.message}`);
          }
        }
        
        console.log(`🔍 All chunks completed! Total latest snapshots: ${allLatestSnapshots.length}`);
        
        // Debug: Log what we received from the backend
        console.log('🔍 Backend response data structure (refresh):', allLatestSnapshots);
        console.log('🔍 Sample snapshot object (refresh):', allLatestSnapshots[0]);
        console.log('🔍 Checking for specific profile 68891240d2845576c459f12c...');
        const specificProfile = allLatestSnapshots.find(s => s && s.profileId === '68891240d2845576c459f12c');
        console.log('🔍 Found specific profile in response (refresh):', specificProfile);
        
        // Debug: Check the data structure more thoroughly
        console.log('🔍 Total snapshots received (refresh):', allLatestSnapshots.length);
        console.log('🔍 First few snapshots (refresh):', allLatestSnapshots.slice(0, 3));
        console.log('🔍 Are all snapshots null? (refresh):', allLatestSnapshots.every(s => s === null));
        console.log('🔍 Non-null snapshots (refresh):', allLatestSnapshots.filter(s => s !== null).length);
        console.log('🔍 Sample non-null snapshot (refresh):', allLatestSnapshots.find(s => s !== null));
        
        // Group snapshots by profile ID - now each profile has only its latest snapshot
        const snapshotsData = {};
        trackedProfiles.forEach(profile => {
          const latestSnapshot = allLatestSnapshots.find(s => s && s.profileId === profile._id);
          // Store as array for consistency, empty array if no snapshot (null from backend)
          snapshotsData[profile._id] = latestSnapshot ? [latestSnapshot] : [];
          
          // Debug: Log what we found for each profile
          if (profile._id === '68891240d2845576c459f12c') {
            console.log(`🔍 DEBUG (refresh): Profile ${profile.name} (${profile._id}) - found snapshot:`, latestSnapshot);
            console.log(`🔍 DEBUG (refresh): Will store as:`, latestSnapshot ? [latestSnapshot] : []);
          }
        });
        
        // Debug: Check final snapshotsData structure
        console.log('🔍 Final snapshotsData keys (refresh):', Object.keys(snapshotsData));
        console.log('🔍 snapshotsData sample entries (refresh):', Object.entries(snapshotsData).slice(0, 3));
        console.log('🔍 Profiles with snapshots in snapshotsData (refresh):', Object.values(snapshotsData).filter(arr => arr.length > 0).length);
        
        // Update cache and state
        snapshotCache.current = snapshotsData;
        setSnapshots(snapshotsData);
        lastFetchTime.current = Date.now();
        setCacheAge(Date.now()); // Update cache age on refresh
        setFetchProgress({ current: trackedProfiles.length, total: trackedProfiles.length });
        
      } catch (err) {
        console.error('Failed to refresh snapshots:', err);
        console.error('Error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: err.message,
          url: err.config?.url,
          params: err.config?.params
        });
        
        // Don't fall back to individual calls - show error instead
        setError(`Failed to refresh snapshots: ${err.response?.data?.error || err.message}`);
        setLoading(false);
      } finally {
        // Always reset the chunking flag
        isChunking.current = false;
      }
    } catch (err) {
      console.error('Error refreshing snapshots:', err);
      setError('Failed to refresh snapshot data');
    } finally {
      setLoading(false);
    }
  }, [profiles, CACHE_DURATION]);

  if (loading) {
    return (
      <Card className="modern-card">
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            {fetchProgress.total > 0 
              ? `Loading snapshot data... (${fetchProgress.current}/${fetchProgress.total} profiles)`
              : 'Loading snapshot data...'
            }
          </Typography>
          {fetchProgress.total > 0 && (
            <Box sx={{ mt: 2, width: '100%', maxWidth: 300, mx: 'auto' }}>
              <LinearProgress 
                variant="determinate" 
                value={(fetchProgress.current / fetchProgress.total) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                {fetchProgress.current === fetchProgress.total 
                  ? 'Processing data...' 
                  : 'Fetching snapshots...'
                }
              </Typography>
            </Box>
          )}
          {cacheAge > 0 && (
            <Typography variant="caption" sx={{ mt: 2, color: 'text.secondary', display: 'block' }}>
              Cache: {Math.round((Date.now() - cacheAge) / 1000)}s old
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="modern-card">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon color="primary" />
              Snapshot Success Tracker
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {cacheAge > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                  Last updated: {Math.round((Date.now() - cacheAge) / 1000)}s ago
                </Typography>
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => handleRefreshData(false)}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => handleRefreshData(true)}
                size="small"
                color="secondary"
              >
                Force Refresh
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {successMessage}
            </Alert>
          )}

          {/* Success Metrics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', color: 'white' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {successMetrics.successRate}%
                </Typography>
                <Typography variant="body2">
                  Success Rate
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'success.main', color: 'white' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {successMetrics.successfulSnapshots}
                </Typography>
                <Typography variant="body2">
                  Successful
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.main', color: 'white' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {successMetrics.partialSnapshots}
                </Typography>
                <Typography variant="body2">
                  Partial
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', p: 2, bgcolor: 'error.main', color: 'white' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {successMetrics.failedSnapshots}
                </Typography>
                <Typography variant="body2">
                  Failed
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Summary Stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Total Tracked Profiles:</strong> {successMetrics.totalProfiles}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Profiles with Snapshots:</strong> {successMetrics.profilesWithSnapshots}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Total Snapshots:</strong> {successMetrics.totalSnapshots}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>All Successful:</strong> {successMetrics.profilesByStatus.allSuccessful.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>All Failed:</strong> {successMetrics.profilesByStatus.allFailed.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Partial/Mixed:</strong> {successMetrics.profilesByStatus.partial.length}
              </Typography>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="error"
              startIcon={<RefreshIcon />}
              onClick={handleRetryAllFailed}
              disabled={successMetrics.profilesByStatus.allFailed.length === 0 && 
                       successMetrics.profilesByStatus.partial.length === 0}
            >
              Retry All Failed ({successMetrics.profilesByStatus.allFailed.length + 
                               successMetrics.profilesByStatus.partial.length})
            </Button>
          </Box>

          {/* Failed Profiles Table */}
          {(successMetrics.profilesByStatus.allFailed.length > 0 || 
            successMetrics.profilesByStatus.partial.length > 0) && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Profiles with Failed Snapshots ({successMetrics.profilesByStatus.allFailed.length + successMetrics.profilesByStatus.partial.length})
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFailedTableExpanded(!failedTableExpanded)}
                  startIcon={failedTableExpanded ? <CloseIcon /> : <TimelineIcon />}
                >
                  {failedTableExpanded ? 'Collapse' : 'Expand'}
                </Button>
              </Box>
              
              {!failedTableExpanded && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {successMetrics.profilesByStatus.allFailed.length} fully failed, {successMetrics.profilesByStatus.partial.length} partial snapshots
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Click "Expand" to view detailed table with actions
                  </Typography>
                </Box>
              )}
              
              {failedTableExpanded && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Business Name</TableCell>
                        <TableCell>Latest Snapshot Status</TableCell>
                        <TableCell>Data Types</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...successMetrics.profilesByStatus.allFailed, 
                         ...successMetrics.profilesByStatus.partial].map((profile) => {
                         const profileSnapshots = snapshots[profile._id] || [];
                         const latestSnapshot = profileSnapshots[0]; // Since we only fetch the latest
                         
                         if (!latestSnapshot) return null;
                         
                         const snapshotSuccess = calculateSnapshotSuccess(latestSnapshot);
                        
                        return (
                          <TableRow key={profile._id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <BusinessIcon color="primary" fontSize="small" />
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {profile.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={snapshotSuccess.isFullyFailed ? 'All Failed' : 'Partial'}
                                color={snapshotSuccess.isFullyFailed ? 'error' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {['business', 'reviews', 'posts', 'qa', 'photos'].map((type) => (
                                  <Tooltip key={type} title={`${type}: ${snapshotSuccess[type] ? '✅' : '❌'}`}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      {type === 'business' && <BusinessIcon fontSize="small" />}
                                      {type === 'reviews' && <StarIcon fontSize="small" />}
                                      {type === 'posts' && <ArticleIcon fontSize="small" />}
                                      {type === 'qa' && <QAIcon fontSize="small" />}
                                      {type === 'photos' && <PhotoIcon fontSize="small" />}
                                      {snapshotSuccess[type] ? (
                                        <CheckCircleIcon color="success" fontSize="small" />
                                      ) : (
                                        <ErrorIcon color="error" fontSize="small" />
                                      )}
                                    </Box>
                                  </Tooltip>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<RefreshIcon />}
                                  onClick={() => handleRetrySnapshots(profile._id)}
                                  disabled={retryingSnapshots.has(profile._id)}
                                >
                                  {retryingSnapshots.has(profile._id) ? 'Retrying...' : 'Retry'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenDetails(profile)}
                                >
                                  Details
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {/* Success Profiles Summary */}
          {successMetrics.profilesByStatus.allSuccessful.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  All Successful Profiles ({successMetrics.profilesByStatus.allSuccessful.length})
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSuccessTableExpanded(!successTableExpanded)}
                  startIcon={successTableExpanded ? <CloseIcon /> : <TimelineIcon />}
                >
                  {successTableExpanded ? 'Collapse' : 'Expand'}
                </Button>
              </Box>
              
              {!successTableExpanded && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {successMetrics.profilesByStatus.allSuccessful.length} profiles with fully successful snapshots
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Click "Expand" to view all successful profiles
                  </Typography>
                </Box>
              )}
              
              {successTableExpanded && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {successMetrics.profilesByStatus.allSuccessful.map((profile) => (
                    <Chip
                      key={profile._id}
                      label={profile.name}
                      color="success"
                      variant="outlined"
                      size="small"
                      icon={<CheckCircleIcon />}
                    />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Profile Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Snapshot Details - {selectedProfile?.name}
            </Typography>
            <IconButton onClick={() => setDetailsOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedProfile && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Snapshot History
              </Typography>
              
              <List>
                {(snapshots[selectedProfile._id] || [])
                  .map((snapshot, index) => {
                    const snapshotSuccess = calculateSnapshotSuccess(snapshot);
                    
                    return (
                      <React.Fragment key={snapshot._id}>
                        <ListItem>
                          <ListItemIcon>
                            {snapshotSuccess.isFullySuccessful ? (
                              <CheckCircleIcon color="success" />
                            ) : snapshotSuccess.isFullyFailed ? (
                              <ErrorIcon color="error" />
                            ) : (
                              <WarningIcon color="warning" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {new Date(snapshot.captureDate).toLocaleString()}
                                </Typography>
                                <Chip
                                  label={snapshotSuccess.isFullySuccessful ? 'All Success' : 
                                         snapshotSuccess.isFullyFailed ? 'All Failed' : 'Partial'}
                                  color={snapshotSuccess.isFullySuccessful ? 'success' : 
                                         snapshotSuccess.isFullyFailed ? 'error' : 'warning'}
                                  size="small"
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Grid container spacing={1}>
                                  {['business', 'reviews', 'posts', 'qa', 'photos'].map((type) => (
                                    <Grid item xs={6} key={type}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {type === 'business' && <BusinessIcon fontSize="small" />}
                                        {type === 'reviews' && <StarIcon fontSize="small" />}
                                        {type === 'posts' && <ArticleIcon fontSize="small" />}
                                        {type === 'qa' && <QAIcon fontSize="small" />}
                                        {type === 'photos' && <PhotoIcon fontSize="small" />}
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                          {type}: {snapshotSuccess[type] ? '✅' : '❌'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            }
                          />
                        </ListItem>
                        {/* No divider needed since we only have one snapshot now */}
                      </React.Fragment>
                    );
                  })}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

SnapshotSuccessTracker.displayName = 'SnapshotSuccessTracker';

export default SnapshotSuccessTracker;